import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { LogLevel } from 'shared/types/logger';

/**
 * Checks if an error is an expected abort error.
 * Abort errors occur when:
 * - User changes project
 * - User logs out
 * - clearProjectData is called
 *
 * These are expected behaviors and should not be logged as errors.
 */
export function isAbortError(
  error: unknown,
  abortController?: AbortController
): boolean {
  if (abortController?.signal.aborted) {
    return true;
  }

  if (error && typeof error === 'object') {
    const err = error as { name?: string; message?: string };

    const isStandardAbortError = err.name === 'AbortError';
    const isCustomAbortFromProcessFetches = err.message === 'Aborted';
    const isSignalAbortedWithoutReason = err.message?.includes('aborted');

    return isStandardAbortError || isCustomAbortFromProcessFetches || !!isSignalAbortedWithoutReason;
  }

  return false;
}

/**
 * Handles abort errors by logging them as INFO (not error) and returning true if handled.
 * Use this in catch blocks to handle expected abort scenarios.
 *
 * @returns true if the error was an abort error (handled), false otherwise
 *
 * @example
 * .catch((e) => {
 *   if (handleAbortError(e, abortController, 'fetching issues')) {
 *     return;
 *   }
 *   swLog('Problem occurred', LogLevel.ERROR, e, null);
 * });
 */
export function handleAbortError(
  error: unknown,
  abortController: AbortController | undefined,
  context: string
): boolean {
  if (isAbortError(error, abortController)) {
    swLog(
      `Operation aborted: ${context}`,
      LogLevel.INFO,
      error,
      null
    );
    return true;
  }
  return false;
}

